import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import { useTheme } from "../../context/ThemeContext";

/**
 * A single plan column on the Plans page. Pure presentational — the parent
 * decides the CTA state from the current plan vs this plan's tier.
 *
 * Props:
 *   plan          — plan object from the catalog
 *   currentTier   — the tenant's current plan tier (number)
 *   isCurrent     — true when this is the active plan
 *   loading       — show a spinner on the CTA while switching
 *   onSelect      — () => void, fired when the CTA is pressed
 */
function NtPlanCard({ plan, currentTier, isCurrent, loading, onSelect }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const isUpgrade = plan.tier > currentTier;
  const priceLabel = plan.priceMonthly === 0 ? "Free" : `$${plan.priceMonthly}`;

  const cta = isCurrent
    ? { label: "Current plan", disabled: true, primary: false }
    : isUpgrade
    ? { label: `Upgrade to ${plan.name}`, disabled: false, primary: true }
    : { label: `Switch to ${plan.name}`, disabled: false, primary: false };

  return (
    <View
      style={[
        styles.card,
        plan.highlight && { borderColor: theme.brand2 },
        isCurrent && { borderColor: theme.green },
      ]}
    >
      {plan.highlight && !isCurrent ? (
        <View style={[styles.ribbon, { backgroundColor: theme.brand2 }]}>
          <NtText style={styles.ribbonText}>RECOMMENDED</NtText>
        </View>
      ) : null}
      {isCurrent ? (
        <View style={[styles.ribbon, { backgroundColor: theme.green }]}>
          <NtText style={styles.ribbonText}>CURRENT</NtText>
        </View>
      ) : null}

      <NtText style={styles.name}>{plan.name}</NtText>

      <View style={styles.priceRow}>
        <NtText style={styles.price}>{priceLabel}</NtText>
        {plan.priceMonthly > 0 ? (
          <NtText style={styles.per}>/mo</NtText>
        ) : null}
      </View>

      <NtText style={styles.tagline}>{plan.tagline}</NtText>

      <View style={styles.divider} />

      <View style={styles.features}>
        {plan.features.map((f, i) => (
          <View key={i} style={styles.featureRow}>
            <NtMaterialIcon name="check" color={theme.green} size={14} />
            <NtText style={styles.featureText}>{f}</NtText>
          </View>
        ))}
      </View>

      <NtTouchableEffect
        onPress={cta.disabled || loading ? undefined : onSelect}
        style={[
          styles.cta,
          cta.primary
            ? { backgroundColor: theme.brand2, borderColor: theme.brand2 }
            : { backgroundColor: theme.surface3, borderColor: theme.border },
          (cta.disabled || loading) && { opacity: 0.6 },
        ]}
      >
        {loading ? (
          <NtActivityIndicator size="small" color={cta.primary ? "#fff" : theme.text} />
        ) : (
          <NtText
            style={[
              styles.ctaText,
              { color: cta.primary ? "#ffffff" : theme.text },
            ]}
          >
            {cta.label}
          </NtText>
        )}
      </NtTouchableEffect>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    card: {
      flex: 1,
      minWidth: 240,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radiusCard,
      padding: 20,
      position: "relative",
    },
    ribbon: {
      position: "absolute",
      top: 14,
      right: 14,
      paddingHorizontal: 9,
      paddingVertical: 3,
      borderRadius: 999,
    },
    ribbonText: {
      color: "#ffffff",
      fontSize: 9,
      fontWeight: "800",
      letterSpacing: 0.6,
    },
    name: { color: t.text, fontSize: 18, fontWeight: "700", letterSpacing: -0.3 },
    priceRow: { flexDirection: "row", alignItems: "baseline", marginTop: 10 },
    price: { color: t.text, fontSize: 30, fontWeight: "800", letterSpacing: -1 },
    per: { color: t.textMute, fontSize: 13, fontWeight: "600", marginLeft: 4 },
    tagline: { color: t.textDim, fontSize: 12.5, marginTop: 8, lineHeight: 18 },
    divider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: t.borderSoft,
      marginVertical: 16,
    },
    features: { gap: 9, marginBottom: 20 },
    featureRow: { flexDirection: "row", alignItems: "center", gap: 9 },
    featureText: { color: t.textDim, fontSize: 12.5, flex: 1 },
    cta: {
      height: 40,
      borderRadius: t.radius,
      borderWidth: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    ctaText: { fontSize: 13, fontWeight: "700" },
  });

export default NtPlanCard;
